import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Search, ChevronUp, ChevronDown, ChevronsUpDown, Loader2 } from 'lucide-react';

interface Column<T = any> {
  id: string;
  header: string;
  accessor: keyof T | ((row: T) => any);
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  render?: (value: any, row: T, index: number) => React.ReactNode;
}

interface DataTableProps<T = any> {
  data: T[];
  columns: Column<T>[];
  className?: string;
  loading?: boolean;
  error?: string | null;
  pageSize?: number;
  virtualScrolling?: boolean;
  onRowClick?: (row: T, index: number) => void;
  onCellClick?: (rowIndex: number, columnKey: string) => void;
  onSelectionChange?: (selectedRows: T[]) => void;
  selectable?: boolean;
  searchable?: boolean;
  emptyMessage?: string;
  filters?: Record<string, any>;
}

const DataTable = <T extends Record<string, any>>({
  data,
  columns,
  className,
  loading = false,
  error = null,
  pageSize = 50,
  virtualScrolling = false,
  onRowClick,
  onCellClick,
  onSelectionChange,
  selectable = false,
  searchable = true,
  emptyMessage = 'No data available',
  filters = {}
}: DataTableProps<T>) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  const filteredAndSortedData = useMemo(() => {
    let result = [...data];

    if (Object.keys(filters).length > 0) {
      result = result.filter(row => {
        return Object.entries(filters).every(([key, value]) => {
          if (!value) return true;
          return String(row[key] || '').toLowerCase().includes(String(value).toLowerCase());
        });
      });
    }

    if (searchTerm) {
      result = result.filter(row =>
        columns.some(column => {
          const value = typeof column.accessor === 'function'
            ? column.accessor(row)
            : row[column.accessor];
          return String(value).toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    if (sortConfig) {
      result.sort((a, b) => {
        const column = columns.find(col => col.id === sortConfig.key);
        if (!column) return 0;

        const aValue = typeof column.accessor === 'function'
          ? column.accessor(a)
          : a[column.accessor];
        const bValue = typeof column.accessor === 'function'
          ? column.accessor(b)
          : b[column.accessor];

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, columns, searchTerm, sortConfig, filters]);

  const paginatedData = useMemo(() => {
    if (virtualScrolling) return filteredAndSortedData;
    
    const startIndex = (currentPage - 1) * pageSize;
    return filteredAndSortedData.slice(startIndex, startIndex + pageSize);
  }, [filteredAndSortedData, currentPage, pageSize, virtualScrolling]);

  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);

  const handleSort = useCallback((columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (!column?.sortable) return;

    setSortConfig(current => {
      if (current?.key === columnId) {
        return current.direction === 'asc'
          ? { key: columnId, direction: 'desc' }
          : null;
      }
      return { key: columnId, direction: 'asc' };
    });
  }, [columns]);

  const handleRowSelection = useCallback((index: number, selected: boolean) => {
    const newSelection = new Set(selectedRows);
    if (selected) {
      newSelection.add(index);
    } else {
      newSelection.delete(index);
    }
    setSelectedRows(newSelection);
    
    const selectedData = Array.from(newSelection).map(i => filteredAndSortedData[i]);
    onSelectionChange?.(selectedData);
  }, [selectedRows, filteredAndSortedData, onSelectionChange]);

  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      const allIndices = new Set(filteredAndSortedData.map((_, i) => i));
      setSelectedRows(allIndices);
      onSelectionChange?.(filteredAndSortedData);
    } else {
      setSelectedRows(new Set());
      onSelectionChange?.([]);
    }
  }, [filteredAndSortedData, onSelectionChange]);

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Card className={`geex-card p-6 text-center ${className || ''}`}>
          <div className="text-red-400 mb-2 text-lg">⚠️ Error</div>
          <p className="text-slate-300">{error}</p>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className={`geex-card overflow-hidden ${className || ''}`}>
        {searchable && (
          <motion.div 
            className="p-8 border-b border-slate-700/50 bg-slate-800/30"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                type="text"
                placeholder="Search across all columns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-900/60 border-slate-600/40 text-white placeholder-slate-400 focus:border-slate-500/50 focus:ring-slate-500/20 h-12"
              />
            </div>
          </motion.div>
        )}

        <div className="overflow-auto" style={{ maxHeight: virtualScrolling ? '500px' : 'none' }}>
          <table className="w-full">
            <thead className="bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
              <tr className="border-b border-slate-700/50">
                {selectable && (
                  <th className="w-12 p-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedRows.size === filteredAndSortedData.length && filteredAndSortedData.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-slate-600 bg-slate-800 text-slate-500 focus:ring-slate-500/30 focus:ring-2"
                    />
                  </th>
                )}
                {columns.map(column => (
                  <th
                    key={column.id}
                    className={`p-4 text-left text-sm font-semibold text-slate-200 select-none ${
                      column.sortable ? 'cursor-pointer hover:bg-slate-800/60 transition-colors duration-200' : ''
                    } ${column.width ? `w-${column.width}` : ''}`}
                    onClick={() => handleSort(column.id)}
                  >
                    <div className="flex items-center gap-2">
                      {column.header}
                      {column.sortable && (
                        <motion.div
                          initial={{ opacity: 0.5 }}
                          whileHover={{ opacity: 1 }}
                          className="text-slate-400"
                        >
                          {sortConfig?.key === column.id ? (
                            sortConfig.direction === 'asc' ? (
                              <ChevronUp size={16} className="text-slate-400" />
                            ) : (
                              <ChevronDown size={16} className="text-slate-400" />
                            )
                          ) : (
                            <ChevronsUpDown size={16} />
                          )}
                        </motion.div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.tr
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <td colSpan={columns.length + (selectable ? 1 : 0)} className="p-8 text-center">
                      <div className="flex items-center justify-center gap-3 text-slate-300">
                        <Loader2 className="animate-spin" size={20} />
                        <span>Loading data...</span>
                      </div>
                    </td>
                  </motion.tr>
                ) : paginatedData.length === 0 ? (
                  <motion.tr
                    key="empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <td colSpan={columns.length + (selectable ? 1 : 0)} className="p-8 text-center text-slate-400">
                      <div className="flex flex-col items-center gap-2">
                        <div className="text-2xl">📭</div>
                        <span>{emptyMessage}</span>
                      </div>
                    </td>
                  </motion.tr>
                ) : (
                  paginatedData.map((row, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      className={`border-b border-slate-700/30 hover:bg-slate-800/40 transition-all duration-200 group ${
                        onRowClick ? 'cursor-pointer hover:bg-slate-800/60' : ''
                      } ${selectedRows.has(index) ? 'bg-slate-500/10 border-slate-500/30 hover:bg-slate-500/20' : ''}`}
                      onClick={() => onRowClick?.(row, index)}
                      whileHover={{ scale: 1.001 }}
                    >
                      {selectable && (
                        <td className="p-4">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(index)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleRowSelection(index, e.target.checked);
                            }}
                            className="rounded border-slate-600 bg-slate-800 text-slate-500 focus:ring-slate-500/30 focus:ring-2"
                          />
                        </td>
                      )}
                      {columns.map(column => {
                        const value = typeof column.accessor === 'function'
                          ? column.accessor(row)
                          : row[column.accessor];
                        
                        return (
                          <td 
                            key={column.id} 
                            className="p-4 text-sm text-white group-hover:text-slate-100 transition-colors duration-200 cursor-pointer hover:bg-slate-700/20"
                            onClick={(e) => {
                              e.stopPropagation();
                              onCellClick?.(index, column.id);
                            }}
                          >
                            {column.render ? column.render(value, row, index) : String(value)}
                          </td>
                        );
                      })}
                    </motion.tr>
                  ))
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {!virtualScrolling && totalPages > 1 && (
          <motion.div 
            className="flex items-center justify-between p-6 border-t border-slate-700/50 bg-slate-800/30"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className="text-sm text-slate-400">
              Showing <span className="text-white font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
              <span className="text-white font-medium">{Math.min(currentPage * pageSize, filteredAndSortedData.length)}</span> of{' '}
              <span className="text-white font-medium">{filteredAndSortedData.length}</span> results
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="violett-button text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </Button>
              <div className="flex items-center gap-1 px-3 py-2 text-sm text-slate-300 bg-slate-800/60 rounded-lg border border-slate-600/40">
                Page <span className="text-white font-medium mx-1">{currentPage}</span> of{' '}
                <span className="text-white font-medium ml-1">{totalPages}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="violett-button text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </Button>
            </div>
          </motion.div>
        )}
      </Card>
    </motion.div>
  );
};

export default DataTable;
